import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { PageLayout } from "../components/layout/Navbar";
import { Card, Badge, Button, Input } from "../components/ui";
import MahallaSelector from "../components/MahallaSelector";
import { getDisplayName } from "../utils/userDisplay";

export default function UserProfile() {
  const { t } = useTranslation();
  const { user, users, updateUser, updateProfile, deleteUser, fetchAllUsers, isLoading } = useAuth();
  const fileRef = useRef(null);
  
  const [error, setError] = useState("");
  const [profileEdit, setProfileEdit] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
  });
  const [mahalla, setMahalla] = useState(
    user?.mahalla ? { id: user.mahalla.id, name: user.mahalla.name } : null
  );


  useEffect(() => {
    if (user?.role === "ADMIN" || user?.role === "OPERATOR") {
      fetchAllUsers();
    }
  }, [user?.role, fetchAllUsers]);

  useEffect(() => {
    setProfileEdit({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
    });
    if (user?.mahalla) setMahalla({ id: user.mahalla.id, name: user.mahalla.name });
    else if (user?.mahallaId) setMahalla((m) => m?.id === user.mahallaId ? m : null);
  }, [user]);

  const onPickAvatar = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      setError(t("profileModal.errorInvalidImage"));
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await updateUser(user.id, { avatarDataUrl: String(reader.result || "") });
        setError("");
      } catch (err) {
        setError(err.message);
      }
    };
    reader.readAsDataURL(file);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!mahalla?.id) {
      setError(t("profileModal.selectMahalla"));
      return;
    }
    try {
      await updateProfile({
        firstName: profileEdit.firstName,
        lastName: profileEdit.lastName,
        fullName: `${profileEdit.firstName} ${profileEdit.lastName}`.trim(),
        mahallaId: mahalla.id,
      });
      setError("");
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <PageLayout>
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="space-y-1 mb-2">
          <h1 className="text-3xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight">
            {t("profileModal.title")} <span className="text-[#1e9f6b]">{t("profileModal.settings")}</span>
          </h1>
          <p className="text-slate-500 dark:text-slate-400 font-bold italic uppercase text-xs tracking-widest">{t("profileModal.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[350px_1fr] gap-8 items-start">
          {/* Left Column: Avatar & Quick Info */}
          <Card className="p-8 sticky top-24 border-0 shadow-2xl shadow-slate-200/50 dark:shadow-none">
            <div className="flex flex-col items-center gap-6">
              <div className="relative group">
                <div className="h-40 w-40 rounded-[40px] overflow-hidden bg-slate-100 dark:bg-slate-800 border-4 border-white dark:border-slate-900 shadow-2xl relative">
                  {(user?.avatarUrl || user?.avatarDataUrl) ? (
                    <img 
                      src={user.avatarUrl || user.avatarDataUrl} 
                      alt="Avatar" 
                      className="h-full w-full object-cover" 
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700">
                      <span className="material-symbols-outlined text-slate-400 text-6xl">person</span>
                    </div>
                  )}
                  <div 
                    onClick={() => fileRef.current?.click()}
                    className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                  >
                    <span className="material-symbols-outlined text-white text-3xl">photo_camera</span>
                  </div>
                </div>
                <button
                  onClick={() => fileRef.current?.click()}
                  className="absolute -bottom-2 -right-2 h-12 w-12 rounded-2xl bg-[#1e9f6b] text-white shadow-xl shadow-[#1e9f6b]/30 flex items-center justify-center hover:scale-110 transition-transform active:scale-95"
                >
                  <span className="material-symbols-outlined text-[20px]">add_a_photo</span>
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPickAvatar(e.target.files?.[0])} />
              </div>

              <div className="text-center space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <h3 className="text-xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight">{getDisplayName(user)}</h3>
                  {user?.isPremium && (
                    <span className="bg-amber-400 text-amber-900 text-[10px] font-black px-2 py-0.5 rounded-full uppercase italic">{t("premiumPanel.premium")}</span>
                  )}
                </div>
                <p className="text-sm font-bold text-slate-500 dark:text-slate-400 tracking-wide">{user?.phone}</p>
                {Boolean(user?.role) && (
                  <Badge variant={user?.role === 'admin' ? 'danger' : 'success'} className="uppercase italic text-[10px] font-black">
                    {user?.role}
                  </Badge>
                )}
              </div>

              <div className="w-full pt-4 space-y-3">
                <div className="flex items-center justify-between p-5 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t("billing.balance.current")}</span>
                  <span className="text-sm font-black text-[#1e9f6b] italic">{(user?.balance ?? 0).toLocaleString('uz-UZ')} UZS</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Right Column: Forms */}
          <div className="space-y-8">
            {/* Shaxsiy ma'lumotlar formasi */}
            <Card className="p-8 border-0 shadow-2xl shadow-slate-200/50 dark:shadow-none">
              <div className="flex items-center gap-3 mb-10">
                <div className="h-12 w-12 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl flex items-center justify-center text-[#1e9f6b]">
                  <span className="material-symbols-outlined">person_outline</span>
                </div>
                <h2 className="text-xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight">{t("profileModal.personalInfo")}</h2>
              </div>

              <form onSubmit={saveProfile} className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] px-1">{t("auth.firstName")}</label>
                  <Input 
                    value={profileEdit.firstName}
                    onChange={(e) => setProfileEdit(p => ({ ...p, firstName: e.target.value }))}
                    className="h-[58px] text-lg font-black italic rounded-2xl"
                    placeholder={t("auth.firstName")}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] px-1">{t("auth.lastName")}</label>
                  <Input 
                    value={profileEdit.lastName}
                    onChange={(e) => setProfileEdit(p => ({ ...p, lastName: e.target.value }))}
                    className="h-[58px] text-lg font-black italic rounded-2xl"
                    placeholder={t("auth.lastName")}
                  />
                </div>
                <div className="md:col-span-2">
                  <MahallaSelector value={mahalla} onChange={setMahalla} required />
                </div>
                <div className="md:col-span-2 pt-6">
                  <Button 
                    type="submit" 
                    loading={isLoading}
                    className="h-14 w-full md:w-auto px-12 rounded-2xl font-black text-sm uppercase tracking-widest shadow-lg shadow-[#1e9f6b]/30 italic"
                  >
                    {t("common.save")}
                  </Button>
                </div>
              </form>
            </Card>

            {/* Admin Management Section */}
            {(user?.role === "admin" || user?.role === "ADMIN") && (
              <Card className="p-8 border-0 shadow-2xl shadow-slate-200/50 dark:shadow-none">
                <div className="flex items-center gap-3 mb-10">
                  <div className="h-12 w-12 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center text-blue-600">
                    <span className="material-symbols-outlined">group_add</span>
                  </div>
                  <h2 className="text-xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight">{t("profileModal.userManagement")}</h2>
                </div>

                <div className="overflow-x-auto -mx-8 px-8">
                  <table className="w-full text-left border-separate border-spacing-y-4">
                    <thead>
                      <tr className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em]">
                        <th className="pb-4 px-4">{t("profileModal.user")}</th>
                        <th className="pb-4 px-4">{t("profileModal.phone")}</th>
                        <th className="pb-4 px-4">{t("profileModal.role")}</th>
                        <th className="pb-4 px-4 text-right">{t("profileModal.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} className="group">
                          <td className="bg-slate-50 dark:bg-slate-800/50 p-5 rounded-l-[24px] border-y border-l border-slate-100 dark:border-slate-800/50 group-hover:bg-white dark:group-hover:bg-slate-700 transition-all duration-300 shadow-sm shadow-slate-200/50 dark:shadow-none">
                            <div className="flex items-center gap-4">
                              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#1e9f6b] to-[#0d7a52] flex items-center justify-center text-white text-xs font-black italic shadow-md shadow-[#1e9f6b]/20">
                                {u.fullName?.[0]}
                              </div>
                              <span className="font-black text-slate-900 dark:text-white italic">{u.fullName}</span>
                            </div>
                          </td>
                          <td className="bg-slate-50 dark:bg-slate-800/50 p-5 border-y border-slate-100 dark:border-slate-800/50 group-hover:bg-white dark:group-hover:bg-slate-700 transition-all duration-300 shadow-sm shadow-slate-200/50 dark:shadow-none">
                            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 tracking-wide">{u.phone}</span>
                          </td>
                          <td className="bg-slate-50 dark:bg-slate-800/50 p-5 border-y border-slate-100 dark:border-slate-800/50 group-hover:bg-white dark:group-hover:bg-slate-700 transition-all duration-300 shadow-sm shadow-slate-200/50 dark:shadow-none">
                            <Badge variant={u.role?.toLowerCase() === 'admin' ? 'danger' : 'info'} className="uppercase text-[9px] font-black italic tracking-wider">
                              {u.role}
                            </Badge>
                          </td>
                          <td className="bg-slate-50 dark:bg-slate-800/50 p-5 rounded-r-[24px] border-y border-r border-slate-100 dark:border-slate-800/50 group-hover:bg-white dark:group-hover:bg-slate-700 transition-all duration-300 shadow-sm shadow-slate-200/50 dark:shadow-none text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button 
                                onClick={() => { if(window.confirm(t("common.confirmDelete"))) deleteUser(u.id) }} 
                                className="h-9 w-9 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-all shadow-sm"
                              >
                                <span className="material-symbols-outlined text-[18px]">delete</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="fixed bottom-24 right-6 max-w-sm bg-red-500 text-white p-4 rounded-3xl shadow-2xl flex items-start gap-3 animate-in slide-in-from-right duration-500 z-[100]">
          <div className="h-8 w-8 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
            <span className="material-symbols-outlined text-[20px]">error</span>
          </div>
          <p className="text-sm font-bold pt-1.5">{error}</p>
        </div>
      )}
    </PageLayout>
  );
}